import { useMemo, useState } from 'react';
import styled from 'styled-components';

/*
 * 일정변경 다이얼 피커
 * - [날짜변경] / [시간변경] 토글
 * - 날짜변경: 월 캘린더 (과거 비활성)
 * - 시간변경: 아날로그 시계 다이얼 (탭/클릭으로 시·분 선택) + 오전/오후
 * - 상단: 기존 예약 일정 / 변경 예정 일정 카드
 * - 하단: 변경 일정 적용 / 취소
 *
 * props:
 *   baseRaw   기존 일정 ISO 문자열 (표시용)
 *   date      'YYYY-MM-DD'  (controlled)
 *   time      'HH:MM' 24h   (controlled, 빈값 가능)
 *   onChange  ({ date, time }) => void
 *   onApply   () => void
 *   onCancel  () => void
 *   applying  bool (제출 중)
 */

const WEEKDAY_KR = ['일', '월', '화', '수', '목', '금', '토'];

function parseRaw(input) {
  if (!input) return null;
  const s = String(input);
  let dt;
  if (s.includes('T') || (s.includes(' ') && s.includes(':'))) dt = new Date(s.replace(' ', 'T'));
  else if (/^\d{4}-\d{2}-\d{2}/.test(s)) dt = new Date(s + 'T00:00:00');
  else dt = new Date(s);
  return Number.isNaN(dt.getTime()) ? null : dt;
}

function ymd(dt) {
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const d = String(dt.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function fmtDateLabel(dateStr) {
  if (!dateStr) return '';
  const dt = parseRaw(dateStr);
  if (!dt) return dateStr;
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const d = String(dt.getDate()).padStart(2, '0');
  return `${y}.${m}.${d} (${WEEKDAY_KR[dt.getDay()]})`;
}

function fmtTimeLabel(timeStr) {
  if (!timeStr) return '';
  const [hh, mm] = timeStr.split(':').map((n) => parseInt(n, 10));
  if (Number.isNaN(hh)) return '';
  const ampm = hh < 12 ? '오전' : '오후';
  const h12 = hh % 12 || 12;
  return `${ampm} ${h12}:${String(mm || 0).padStart(2, '0')}`;
}

// 시계 좌표 계산 (12시 = top, 시계방향)
function polar(cx, cy, r, angleDeg) {
  const rad = ((angleDeg - 90) * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

export default function ScheduleDialPicker({
  baseRaw,
  date,
  time,
  onChange,
  onApply,
  onCancel,
  applying,
}) {
  const baseDt = useMemo(() => parseRaw(baseRaw), [baseRaw]);
  const [mode, setMode] = useState('date'); // 'date' | 'time'
  const [selecting, setSelecting] = useState('hour'); // 'hour' | 'minute'

  // 캘린더 표시 월
  const initCal = baseDt || new Date();
  const [calYear, setCalYear] = useState(initCal.getFullYear());
  const [calMonth, setCalMonth] = useState(initCal.getMonth()); // 0-based

  // 시간 파싱 (기본 09:00)
  const [hh24, mm] = useMemo(() => {
    if (time && /^\d{1,2}:\d{2}$/.test(time)) {
      const [a, b] = time.split(':').map((n) => parseInt(n, 10));
      return [a, b];
    }
    return [9, 0];
  }, [time]);

  const ampm = hh24 < 12 ? 'AM' : 'PM';
  const hour12 = hh24 % 12 || 12;

  const setTime24 = (h12, m, ap) => {
    let h = h12 % 12;
    if (ap === 'PM') h += 12;
    onChange({ date, time: `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}` });
  };

  const pickHour = (h12) => {
    setTime24(h12, mm, ampm);
    setSelecting('minute');
  };
  const pickMinute = (m) => setTime24(hour12, m, ampm);
  const pickAmPm = (ap) => setTime24(hour12, mm, ap);

  // 캘린더 그리드
  const calCells = useMemo(() => {
    const first = new Date(calYear, calMonth, 1);
    const startDow = first.getDay();
    const daysInMonth = new Date(calYear, calMonth + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < startDow; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);
    return cells;
  }, [calYear, calMonth]);

  const todayStr = ymd(new Date());

  const moveMonth = (delta) => {
    let m = calMonth + delta;
    let y = calYear;
    if (m < 0) { m = 11; y -= 1; }
    if (m > 11) { m = 0; y += 1; }
    setCalMonth(m);
    setCalYear(y);
  };

  const pickDay = (d) => {
    if (!d) return;
    onChange({ date: ymd(new Date(calYear, calMonth, d)), time });
  };

  // 시계 SVG 상수
  const SZ = 280;
  const C = SZ / 2;
  const R_HOUR = 78;
  const R_MIN = 118;
  const handAngle = selecting === 'minute' ? (mm / 5) * 30 : (hour12 % 12) * 30;
  const handR = selecting === 'minute' ? R_MIN - 8 : R_HOUR;
  const handPos = polar(C, C, handR, handAngle);

  const changedLabel = date
    ? `${fmtDateLabel(date)}${time ? ` ${fmtTimeLabel(time)}` : ''}`
    : '날짜를 선택해주세요';

  return (
    <Wrap>
      {/* 기존 / 변경 예정 일정 */}
      <SummaryCard>
        <SumLabel>기존 예약 일정</SumLabel>
        <SumValue>{fmtDateLabel(baseRaw && ymd(baseDt))}{baseDt && fmtTimeLabel(`${String(baseDt.getHours()).padStart(2, '0')}:${String(baseDt.getMinutes()).padStart(2, '0')}`) ? ` ${fmtTimeLabel(`${String(baseDt.getHours()).padStart(2, '0')}:${String(baseDt.getMinutes()).padStart(2, '0')}`)}` : ''}</SumValue>
      </SummaryCard>
      <SummaryCard $accent>
        <SumLabel>변경 예정 일정</SumLabel>
        <SumValue $accent>{changedLabel}</SumValue>
      </SummaryCard>

      {/* 토글 */}
      <ToggleRow>
        <ToggleBtn type="button" $on={mode === 'date'} onClick={() => setMode('date')}>날짜변경</ToggleBtn>
        <ToggleBtn type="button" $on={mode === 'time'} onClick={() => setMode('time')}>시간변경</ToggleBtn>
      </ToggleRow>

      {/* 날짜변경 — 캘린더 */}
      {mode === 'date' && (
        <Panel>
          <CalHead>
            <NavBtn type="button" onClick={() => moveMonth(-1)} aria-label="이전 달">‹</NavBtn>
            <CalTitle>{calYear}년 {calMonth + 1}월</CalTitle>
            <NavBtn type="button" onClick={() => moveMonth(1)} aria-label="다음 달">›</NavBtn>
          </CalHead>
          <CalGrid>
            {WEEKDAY_KR.map((w, i) => (
              <Dow key={w} $sun={i === 0} $sat={i === 6}>{w}</Dow>
            ))}
            {calCells.map((d, i) => {
              if (!d) return <span key={`e${i}`} />;
              const cellStr = ymd(new Date(calYear, calMonth, d));
              const isPast = cellStr < todayStr;
              const isSel = cellStr === date;
              const dow = new Date(calYear, calMonth, d).getDay();
              return (
                <DayCell
                  key={cellStr}
                  type="button"
                  $sel={isSel}
                  $past={isPast}
                  $sun={dow === 0}
                  $sat={dow === 6}
                  disabled={isPast}
                  onClick={() => pickDay(d)}
                >
                  {d}
                </DayCell>
              );
            })}
          </CalGrid>
        </Panel>
      )}

      {/* 시간변경 — 시계 다이얼 */}
      {mode === 'time' && (
        <Panel>
          <ClockWrap>
            <svg width="100%" viewBox={`0 0 ${SZ} ${SZ}`} style={{ maxWidth: 300 }}>
              <circle cx={C} cy={C} r={C - 2} fill="#F4F6FF" />
              {/* 핸드 */}
              <line x1={C} y1={C} x2={handPos.x} y2={handPos.y} stroke="#4A6CF7" strokeWidth="2.5" />
              <circle cx={C} cy={C} r="4" fill="#4A6CF7" />
              <circle cx={handPos.x} cy={handPos.y} r="17" fill="#4A6CF7" />

              {/* 분 링 (바깥, 5분 단위) */}
              {Array.from({ length: 12 }, (_, i) => i * 5).map((m) => {
                const p = polar(C, C, R_MIN, (m / 5) * 30);
                const on = selecting === 'minute' && m === mm;
                return (
                  <g key={`m${m}`} onClick={() => pickMinute(m)} style={{ cursor: 'pointer' }}>
                    <circle cx={p.x} cy={p.y} r="13" fill="transparent" />
                    <text x={p.x} y={p.y} textAnchor="middle" dominantBaseline="central"
                      fontSize="11" fontWeight={on ? 700 : 500} fill={on ? '#4A6CF7' : '#9CA3AF'}>
                      {String(m).padStart(2, '0')}
                    </text>
                  </g>
                );
              })}

              {/* 시 링 (안쪽) */}
              {Array.from({ length: 12 }, (_, i) => i + 1).map((h) => {
                const p = polar(C, C, R_HOUR, (h % 12) * 30);
                const on = selecting === 'hour' && h === hour12;
                return (
                  <g key={`h${h}`} onClick={() => pickHour(h)} style={{ cursor: 'pointer' }}>
                    <circle cx={p.x} cy={p.y} r="16" fill="transparent" />
                    <text x={p.x} y={p.y} textAnchor="middle" dominantBaseline="central"
                      fontSize="16" fontWeight={on ? 800 : 600} fill={on ? '#fff' : '#374151'}>
                      {h}
                    </text>
                  </g>
                );
              })}
            </svg>

            {/* 중앙 오전/오후 + 시간 입력 */}
            <ClockCenter>
              <AmPmRow>
                <AmPmBtn type="button" $on={ampm === 'AM'} onClick={() => pickAmPm('AM')}>오전</AmPmBtn>
                <AmPmBtn type="button" $on={ampm === 'PM'} onClick={() => pickAmPm('PM')}>오후</AmPmBtn>
              </AmPmRow>
              <TimeDisplay>
                <TimeSeg type="button" $on={selecting === 'hour'} onClick={() => setSelecting('hour')}>
                  {String(hour12).padStart(2, '0')}
                </TimeSeg>
                <Colon>:</Colon>
                <TimeSeg type="button" $on={selecting === 'minute'} onClick={() => setSelecting('minute')}>
                  {String(mm).padStart(2, '0')}
                </TimeSeg>
              </TimeDisplay>
            </ClockCenter>
          </ClockWrap>
        </Panel>
      )}

      {/* 적용 / 취소 */}
      <BtnRow>
        <ApplyBtn type="button" onClick={onApply} disabled={applying || !date}>
          {applying ? '적용 중...' : '변경 일정 적용'}
        </ApplyBtn>
        <CancelBtn type="button" onClick={onCancel} disabled={applying}>취소</CancelBtn>
      </BtnRow>
    </Wrap>
  );
}

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SummaryCard = styled.div`
  background: ${({ $accent }) => ($accent ? '#fff' : '#F9FAFB')};
  border: 1px solid ${({ $accent }) => ($accent ? '#C7D2FE' : '#EEF2F7')};
  border-radius: 14px;
  padding: 14px 16px;
  text-align: center;
`;

const SumLabel = styled.div`
  font-size: 12px;
  color: #9CA3AF;
  font-weight: 600;
  margin-bottom: 4px;
`;

const SumValue = styled.div`
  font-size: 18px;
  font-weight: 800;
  color: ${({ $accent }) => ($accent ? '#4A6CF7' : '#1F2937')};
`;

const ToggleRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
`;

const ToggleBtn = styled.button`
  border: 1.5px solid ${({ $on }) => ($on ? '#4A6CF7' : '#E5E7EB')};
  background: ${({ $on }) => ($on ? '#4A6CF7' : '#fff')};
  color: ${({ $on }) => ($on ? '#fff' : '#6B7280')};
  font-size: 14px;
  font-weight: 700;
  padding: 11px 0;
  border-radius: 10px;
  cursor: pointer;
`;

const Panel = styled.div`
  background: #fff;
  border: 1px solid #EEF2F7;
  border-radius: 14px;
  padding: 14px;
`;

const CalHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
`;

const NavBtn = styled.button`
  border: 0;
  background: #F3F4F6;
  color: #4B5563;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  font-size: 18px;
  font-weight: 700;
  cursor: pointer;
  line-height: 1;
`;

const CalTitle = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: #1F2937;
`;

const CalGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 2px;
`;

const Dow = styled.div`
  text-align: center;
  font-size: 11px;
  font-weight: 600;
  padding: 4px 0;
  color: ${({ $sun, $sat }) => ($sun ? '#EF4444' : $sat ? '#3B82F6' : '#9CA3AF')};
`;

const DayCell = styled.button`
  aspect-ratio: 1;
  border: 0;
  border-radius: 8px;
  background: ${({ $sel }) => ($sel ? '#4A6CF7' : 'transparent')};
  color: ${({ $sel, $past, $sun, $sat }) =>
    $sel ? '#fff' : $past ? '#D1D5DB' : $sun ? '#EF4444' : $sat ? '#3B82F6' : '#374151'};
  font-size: 14px;
  font-weight: ${({ $sel }) => ($sel ? 800 : 500)};
  cursor: ${({ $past }) => ($past ? 'default' : 'pointer')};

  &:hover:not(:disabled) { background: ${({ $sel }) => ($sel ? '#4A6CF7' : '#EEF2FF')}; }
`;

const ClockWrap = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  user-select: none;
`;

const ClockCenter = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  pointer-events: none;
`;

const AmPmRow = styled.div`
  display: flex;
  gap: 4px;
  pointer-events: auto;
`;

const AmPmBtn = styled.button`
  border: 0;
  border-radius: 6px;
  padding: 3px 9px;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  background: ${({ $on }) => ($on ? '#4A6CF7' : '#E5E7EB')};
  color: ${({ $on }) => ($on ? '#fff' : '#6B7280')};
`;

const TimeDisplay = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  pointer-events: auto;
`;

const TimeSeg = styled.button`
  border: 1.5px solid ${({ $on }) => ($on ? '#4A6CF7' : '#E5E7EB')};
  background: #fff;
  color: #1F2937;
  font-size: 16px;
  font-weight: 800;
  width: 36px;
  height: 30px;
  border-radius: 6px;
  cursor: pointer;
`;

const Colon = styled.div`
  font-size: 16px;
  font-weight: 800;
  color: #1F2937;
`;

const BtnRow = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 10px;
  margin-top: 4px;
`;

const ApplyBtn = styled.button`
  min-height: 50px;
  border: 0;
  border-radius: 12px;
  background: #4A6CF7;
  color: #fff;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  &:hover:not(:disabled) { background: #3F5BD9; }
  &:disabled { opacity: 0.6; cursor: default; }
`;

const CancelBtn = styled.button`
  min-height: 50px;
  border: 0;
  border-radius: 12px;
  background: #E5E7EB;
  color: #4B5563;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  &:hover:not(:disabled) { background: #D1D5DB; }
  &:disabled { opacity: 0.6; cursor: default; }
`;
